import { useState } from "react";
import { mockProjects } from "../mock";
import { SEO } from "../components/SEO";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

export const Projects = () => {
  const [filter, setFilter] = useState("all");

  const categories = ["all", "Motorised Pergola", "Glass Veranda", "Pergola Installation"];

  const filteredProjects =
    filter === "all"
      ? mockProjects
      : mockProjects.filter((p) => p.category === filter);

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Pergola Installation Projects Gallery | London & Kent | Hudson Knox"
        description="View our portfolio of completed pergola installations. Motorised louvered pergolas, glass verandas, and outdoor structures across South East England. Real project examples and inspiration."
        keywords="pergola projects, pergola gallery, pergola examples, installed pergolas, completed projects, London pergolas, Kent installations"
        canonicalPath="/projects"
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-neutral-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Our Projects</h1>
          <p className="text-xl text-neutral-200">Luxury outdoor living installations across South East England</p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-12 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? "default" : "outline"}
                className={
                  filter === category
                    ? "bg-neutral-900 text-white"
                    : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                }
              >
                {category === "all" ? "All Projects" : category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="border-neutral-200 overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-64 overflow-hidden group">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-sm font-medium mb-1">{project.category}</p>
                    <h3 className="text-xl font-medium">{project.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-sm text-neutral-500 mb-3">{project.location}</p>
                  <p className="text-neutral-600 mb-4">{project.description}</p>
                  <div className="space-y-1">
                    {project.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-neutral-600">
                        <div className="w-1.5 h-1.5 bg-neutral-900 rounded-full" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-neutral-600 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light mb-6">Ready to Transform Your Garden?</h2>
          <p className="text-xl text-neutral-300 mb-8">
            Let's discuss your outdoor living project. Request a free consultation and quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact">
              <Button size="lg" className="bg-white text-neutral-900 hover:bg-neutral-100">
                Request Free Quote
              </Button>
            </a>
            <a href="tel:07846923387">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900">
                Call 07846 923387
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
